import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

const CW = 1024;
const CH = 1024;
const API_BASE = '/api/annotate';

function RatePage() {
  const [searchParams] = useSearchParams();
  const canvasRef = useRef(null);

  const prolificPid = searchParams.get('prolificPid') || searchParams.get('PROLIFIC_PID') || '';
  const studyId = searchParams.get('studyId') || searchParams.get('STUDY_ID') || '';
  const sessionId = searchParams.get('sessionId') || searchParams.get('SESSION_ID') || '';
  const batchSize = parseInt(searchParams.get('batchSize') || '25', 10);

  const [scenes, setScenes] = useState([]);
  const [sceneIdx, setSceneIdx] = useState(0);
  const [bgImage, setBgImage] = useState(null);
  const [objImages, setObjImages] = useState({});
  const [items, setItems] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState('Loading...');
  const [currentEnv, setCurrentEnv] = useState('');

  // Load scenes
  useEffect(() => {
    const seed = prolificPid || 'default';
    fetch(`${API_BASE}/suggested_scenes?batchStart=0&batchSize=${batchSize}&seed=${seed}&rateOnly=true`)
      .then(r => r.json())
      .then(d => {
        const s = d.scenes || [];
        setScenes(s);
        if (s.length > 0) loadScene(s[0]);
        else setStatus('No scenes available');
      });
  }, []);

  const loadBg = (name) => {
    return new Promise(resolve => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { setBgImage(img); resolve(img); };
      img.src = `${API_BASE}/scene/${name}`;
    });
  };

  const loadObjImg = (label) => {
    return new Promise(resolve => {
      if (objImages[label]) { resolve(objImages[label]); return; }
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => { setObjImages(prev => ({ ...prev, [label]: img })); resolve(img); };
      img.onerror = () => resolve(null);
      img.src = `${API_BASE}/object/${label}`;
    });
  };

  const loadScene = async (scene) => {
    setCurrentEnv((scene.environment || '').replace(/_/g, ' '));
    await loadBg(scene.scene || 'hardwood_room');
    await Promise.all((scene.items || []).map(i => loadObjImg(i.label)));
    setItems(scene.items || []);
    setStatus('');
  };

  // Draw
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, CW, CH);
    if (bgImage) ctx.drawImage(bgImage, 0, 0, CW, CH);
    else { ctx.fillStyle = '#333'; ctx.fillRect(0, 0, CW, CH); }

    [...items].sort((a, b) => (a.layer || 0) - (b.layer || 0)).forEach(item => {
      const img = objImages[item.label];
      if (!img) return;
      const h = CH * item.scale / 100;
      const w = h * (img.width / img.height);
      ctx.drawImage(img, CW * item.x / 100 - w / 2, CH * item.y / 100 - h / 2, w, h);
    });
  }, [items, bgImage, objImages]);

  useEffect(() => { draw(); }, [draw]);

  const rate = (verdict) => {
    const scene = scenes[sceneIdx];
    fetch(`${API_BASE}/label`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        scene: scene.scene || 'hardwood_room',
        verdict,
        items: scene.items || [],
        workerId: prolificPid,
        hitId: studyId,
        assignmentId: sessionId,
        phase: 'rate_only',
        scene_id: scene.scene_id || '',
      }),
    });

    const next = sceneIdx + 1;
    if (next >= scenes.length) {
      setSubmitted(true);
      if (prolificPid) {
        window.location.href = 'https://app.prolific.com/submissions/complete?cc=VIGR_COMPLETE';
      }
      return;
    }
    setSceneIdx(next);
    loadScene(scenes[next]);
  };

  if (submitted) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', fontFamily: 'system-ui' }}>
        <div style={{ textAlign: 'center' }}><h2>Thank you!</h2><p>Your responses have been submitted.</p></div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '100vh',
                  fontFamily: 'system-ui, sans-serif', background: '#f5f5f5', padding: '1rem' }}>
      {/* Progress */}
      <div style={{ width: '100%', maxWidth: 700, marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#666', marginBottom: 4 }}>
          <span>Scene {sceneIdx + 1} of {scenes.length}</span>
          {currentEnv && <span style={{ color: '#1565c0', fontWeight: 600 }}>{currentEnv}</span>}
        </div>
        <div style={{ height: 4, background: '#ccc', borderRadius: 2 }}>
          <div style={{ height: '100%', background: '#2196f3', borderRadius: 2,
            width: `${(sceneIdx / Math.max(scenes.length, 1)) * 100}%`, transition: 'width 0.3s' }} />
        </div>
      </div>

      {/* Instructions with per-category reference images */}
      <div style={{ maxWidth: 700, width: '100%', marginBottom: '0.75rem', padding: '0.5rem 0.75rem',
                    background: '#fff8e1', borderRadius: 8, border: '1px solid #ffe082', fontSize: '0.8rem', color: '#555' }}>
        <div style={{ marginBottom: 6 }}>
          Does this look like a realistic <strong>{currentEnv || 'room'}</strong>? Mark <strong style={{ color: '#4caf50' }}>Good</strong> if all objects are
          in natural positions. Mark <strong style={{ color: '#f44336' }}>Bad</strong> if objects are floating, too large, or in impossible positions.
        </div>
        {currentEnv && (
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <img src={'/api/annotate/scene/ref_' + currentEnv.replace(/ /g, '_') + '_good'}
                alt="Good example" style={{ width: '100%', borderRadius: 4, border: '2px solid #4caf50' }}
                onError={e => e.target.style.display = 'none'} />
              <div style={{ fontSize: '0.7rem', color: '#4caf50', fontWeight: 600 }}>GOOD example</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <img src={'/api/annotate/scene/ref_' + currentEnv.replace(/ /g, '_') + '_bad'}
                alt="Bad example" style={{ width: '100%', borderRadius: 4, border: '2px solid #f44336' }}
                onError={e => e.target.style.display = 'none'} />
              <div style={{ fontSize: '0.7rem', color: '#f44336', fontWeight: 600 }}>BAD example</div>
            </div>
          </div>
        )}
      </div>

      {/* Canvas */}
      <canvas ref={canvasRef} width={CW} height={CH}
        style={{ maxWidth: 700, width: '100%', border: '1px solid #ccc', borderRadius: 4 }} />

      {/* Buttons */}
      <div style={{ display: 'flex', gap: 16, marginTop: '1rem', maxWidth: 700, width: '100%' }}>
        <button onClick={() => rate('good')}
          style={{ flex: 1, padding: '1rem', fontSize: '1.2rem', fontWeight: 700, borderRadius: 8,
                   border: 'none', background: '#4caf50', color: 'white', cursor: 'pointer' }}>
          Good
        </button>
        <button onClick={() => rate('bad')}
          style={{ flex: 1, padding: '1rem', fontSize: '1.2rem', fontWeight: 700, borderRadius: 8,
                   border: 'none', background: '#f44336', color: 'white', cursor: 'pointer' }}>
          Bad
        </button>
      </div>

      {status && <div style={{ marginTop: '0.5rem', color: '#888', fontSize: '0.8rem' }}>{status}</div>}
    </div>
  );
}

export default RatePage;
